import { Group, Path, Rect, Text } from 'react-konva';
import { Size } from '../../../../core/types/ui/size';
import { IPosition2d } from '../../../../core/types/ui/position';
import { useColorModeValue } from '@chakra-ui/react';
import { UseSheetTheme } from '../../../hooks/useSheetTheme';
import CloseIcon from '../../../../core/assets/icons/close.svg';
import MoreIcon from '../../../../core/assets/icons/more.svg';
import UseKonvaImage from '../../../../core/hooks/UseKonvaImage';
import useKonvaCursor, {
  CursorTypes,
  UseKonvaCursor,
} from '../../../../core/hooks/useKonvaCursor';
import { UseFilterDropdown } from '../../../hooks/useFilterDropdown';
import CanvasSVGIcon from '../../../../core/components/canvasIcons/CanvasSVGIcon';

const PADDING = 3;
const MORE_ICON_SIZE = 10; // Konva elements are generally smaller in size
const CLOSE_ICON_SIZE = 10;

interface ColumnFilterCellProps
  extends UseKonvaCursor,
    Size,
    Pick<UseFilterDropdown, 'openFilterDropdown'>,
    IPosition2d,
    Pick<
      UseSheetTheme,
      | 'isRtl'
      | 'fill'
      | 'secondaryFill'
      | 'filterAppliedFill'
      | 'stroke'
      | 'textColor'
    > {
  zoom: number;
  index: number;
}

const ColumnFilterCell: React.FC<ColumnFilterCellProps> = ({
  x,
  y,
  width,
  height,
  index,
  zoom,

  isRtl,
  fill,
  secondaryFill,
  filterAppliedFill,
  stroke,
  textColor,
  openFilterDropdown,
  onMouseEnter,
  onMouseLeave,
}) => {
  const filterText = ''; //index % 2 === 0 ? 'John Doe 42343242342dasdas3' : '';
  const borderColor = useColorModeValue('#d9dddf', '#242c3a');

  const { input, moreIcon, closeIcon } = filterBoxDimensions(
    width,
    height,
    isRtl
  );

  const background = () => (
    <Rect
      width={width}
      height={height}
      fill={filterText.length > 0 ? filterAppliedFill : secondaryFill}
      stroke={stroke}
      strokeWidth={1 * zoom}
      hitStrokeWidth={0}
    />
  );

  const inputView = () => (
    <Rect
      height={input.height}
      width={input.width}
      stroke={borderColor}
      strokeWidth={1}
      hitStrokeWidth={0}
      fill={fill}
      cornerRadius={5}
    />
  );

  const textView = () => (
    <Text
      height={input.height}
      width={input.width - closeIcon.width - PADDING}
      text={filterText}
      verticalAlign="middle"
      wrap="none"
      fontSize={12 * zoom}
      fill={textColor}
      align={isRtl ? 'right' : 'left'}
      x={isRtl ? closeIcon.width + PADDING : PADDING}
      onMouseEnter={(e) => onMouseEnter(e, CursorTypes.Text)}
      onMouseLeave={onMouseLeave}
      ellipsis={true}
    />
  );

  const renderCloseIcon = () => {
    const { x, y, height, width } = closeIcon;

    return (
      <CanvasSVGIcon
        x={x}
        y={y}
        width={width}
        height={height}
        paths={[
          'M12.15 28.012v-0.85c0.019-0.069 0.050-0.131 0.063-0.2 0.275-1.788 1.762-3.2 3.506-3.319 1.95-0.137 3.6 0.975 4.137 2.787 0.069 0.238 0.119 0.488 0.181 0.731v0.85c-0.019 0.056-0.050 0.106-0.056 0.169-0.269 1.65-1.456 2.906-3.081 3.262-0.125 0.025-0.25 0.063-0.375 0.094h-0.85c-0.056-0.019-0.113-0.050-0.169-0.056-1.625-0.262-2.862-1.419-3.237-3.025-0.037-0.156-0.081-0.3-0.119-0.444zM20.038 3.988l-0 0.85c-0.019 0.069-0.050 0.131-0.056 0.2-0.281 1.8-1.775 3.206-3.538 3.319-1.944 0.125-3.588-1-4.119-2.819-0.069-0.231-0.119-0.469-0.175-0.7v-0.85c0.019-0.056 0.050-0.106 0.063-0.162 0.3-1.625 1.244-2.688 2.819-3.194 0.206-0.069 0.425-0.106 0.637-0.162h0.85c0.056 0.019 0.113 0.050 0.169 0.056 1.631 0.269 2.863 1.419 3.238 3.025 0.038 0.15 0.075 0.294 0.113 0.437zM20.037 15.575v0.85c-0.019 0.069-0.050 0.131-0.063 0.2-0.281 1.794-1.831 3.238-3.581 3.313-1.969 0.087-3.637-1.1-4.106-2.931-0.050-0.194-0.094-0.387-0.137-0.581v-0.85c0.019-0.069 0.050-0.131 0.063-0.2 0.275-1.794 1.831-3.238 3.581-3.319 1.969-0.094 3.637 1.1 4.106 2.931 0.050 0.2 0.094 0.394 0.137 0.588z',
        ]}
        viewBoxSize={32}
        fill={'red'}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={(e) => {}}
      />
    );
  };

  const renderMoreIcon = () => {
    const { x, y, width, height } = moreIcon;
    return (
      <CanvasSVGIcon
        x={x}
        y={y}
        width={width}
        height={height}
        paths={[
          'M388.8 896.4v-27.198c.6-2.2 1.6-4.2 2-6.4 8.8-57.2 56.4-102.4 112.199-106.2 62.4-4.4 115.2 31.199 132.4 89.199 2.2 7.6 3.8 15.6 5.8 23.4v27.2c-.6 1.8-1.6 3.399-1.8 5.399-8.6 52.8-46.6 93-98.6 104.4-4 .8-8 2-12 3h-27.2c-1.8-.6-3.6-1.6-5.4-1.8-52-8.4-91.599-45.4-103.6-96.8-1.2-5-2.6-9.6-3.8-14.2zm252.4-768.797l-.001 27.202c-.6 2.2-1.6 4.2-1.8 6.4-9 57.6-56.8 102.6-113.2 106.2-62.2 4-114.8-32-131.8-90.2-2.2-7.401-3.8-15-5.6-22.401v-27.2c.6-1.8 1.6-3.4 2-5.2 9.6-52 39.8-86 90.2-102.2 6.6-2.2 13.6-3.4 20.4-5.2h27.2c1.8.6 3.6 1.6 5.4 1.8 52.2 8.6 91.6 45.4 103.6 96.8 1.201 4.8 2.401 9.4 3.601 13.999zm-.001 370.801v27.2c-.6 2.2-1.6 4.2-2 6.4-9 57.4-58.6 103.6-114.6 106-63 2.8-116.4-35.2-131.4-93.8-1.6-6.2-3-12.4-4.4-18.6v-27.2c.6-2.2 1.6-4.2 2-6.4 8.8-57.4 58.6-103.601 114.6-106.2 63-3 116.4 35.2 131.4 93.8 1.6 6.4 3 12.6 4.4 18.8z',
          // 'M12 7C12.55 7 13 6.55 13 6C13 5.45 12.55 5 12 5C11.45 5 11 5.45 11 6C11 6.55 11.45 7 12 7Z',
          // 'M12 13C12.55 13 13 12.55 13 12C13 11.45 12.55 11 12 11C11.45 11 11 11.45 11 12C11 12.55 11.45 13 12 13Z',
          // 'M12 19C12.55 19 13 18.55 13 18C13 17.45 12.55 17 12 17C11.45 17 11 17.45 11 18C11 18.55 11.45 19 12 19Z',
        ]}
        viewBoxSize={1024}
        fill={textColor}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={(e) => openFilterDropdown(e, index)}
      />
    );
  };

  const filterView = () => (
    <Group x={input.x} y={input.y}>
      {input.isVisible && inputView()}
      {input.isVisible && textView()}
      {closeIcon.isVisible && !!filterText.length && renderCloseIcon()}
    </Group>
  );

  return (
    <Group x={x} y={y}>
      {background()}
      {moreIcon.isVisible && renderMoreIcon()}
      {filterView()}
    </Group>
  );
};

// #region helper
type BoxVisible = { isVisible: boolean };
interface FilterBoxDimensions {
  input: Size & IPosition2d & BoxVisible;
  moreIcon: Size & IPosition2d & BoxVisible;
  closeIcon: Size & IPosition2d & BoxVisible;
}
const filterBoxDimensions = (
  width: number,
  height: number,
  isRtl: boolean
): FilterBoxDimensions => {
  const inputWidth = Math.floor(width - PADDING * 2 - MORE_ICON_SIZE);
  const inputHeight = Math.floor(height - PADDING * 2);

  const moreIconX = width - MORE_ICON_SIZE - PADDING;
  const moreIconY = Math.floor((height - MORE_ICON_SIZE) / 2);

  const closeIconX = inputWidth - PADDING - CLOSE_ICON_SIZE;
  const closeIconY = Math.floor((inputHeight - CLOSE_ICON_SIZE) / 2);

  const inputRtlX = width - inputWidth - PADDING;
  const moreIconRtlX = PADDING;
  const closeIconRtlX = inputRtlX + PADDING - CLOSE_ICON_SIZE;

  return {
    input: {
      x: isRtl ? inputRtlX : PADDING,
      y: PADDING,
      width: inputWidth,
      height: inputHeight,
      isVisible: inputWidth > 0,
    },
    moreIcon: {
      x: isRtl ? moreIconRtlX : Math.max(moreIconX, 0),
      y: moreIconY,
      width: MORE_ICON_SIZE,
      height: MORE_ICON_SIZE,
      isVisible: true,
    },
    closeIcon: {
      x: isRtl ? closeIconRtlX : closeIconX,
      y: closeIconY,
      width: CLOSE_ICON_SIZE,
      height: CLOSE_ICON_SIZE,
      isVisible: inputWidth > 0 && closeIconX > 0,
    },
  };
};
// #endregion

export default ColumnFilterCell;
